import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { LogOut, History, GraduationCap, AlertTriangle, Clock, User as UserIcon } from 'lucide-react';
import { StatusBadge } from '@/components/StatusBadge';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { cs, enUS } from 'date-fns/locale';

interface StationClaim {
  id: string;
  station_id: string;
  released_at: string | null;
  station: {
    station_number: number;
    name: string | null;
  };
}

interface WorkflowHeaderProps {
  vin: string;
  status: string;
  beginnerMode: boolean;
  onBeginnerModeChange: (checked: boolean) => void;
  onReleaseClick: () => void;
  onHistoryClick: () => void;
  isReleasing: boolean;
  stationClaim: StationClaim | null;
  claimedAt: string | null;
  isCarOwner: boolean;
  hasStation: boolean;
}

export const WorkflowHeader = ({
  vin,
  status,
  beginnerMode,
  onBeginnerModeChange,
  onReleaseClick,
  onHistoryClick,
  isReleasing,
  stationClaim,
  claimedAt,
  isCarOwner,
  hasStation,
}: WorkflowHeaderProps) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('cs') ? cs : enUS;

  const formatRelativeTime = (date: string | null) => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: dateLocale });
    } catch {
      return '';
    }
  };

  return (
    <div className="px-4 py-2">
      <div className="flex items-center justify-between gap-3">
        {/* Left: Actions */}
        <div className="flex gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onReleaseClick}
            disabled={isReleasing}
            className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8"
          >
            <LogOut className="mr-1.5 h-3.5 w-3.5" />
            {t('workflow.releaseAndExit')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onHistoryClick}
            className="h-8"
          >
            <History className="mr-1.5 h-3.5 w-3.5" />
            {t('workflow.history')}
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 border rounded-md bg-background h-8">
                  <GraduationCap className="h-3.5 w-3.5 text-primary" />
                  <Label htmlFor="beginner-mode" className="text-xs font-medium cursor-pointer whitespace-nowrap">
                    {t('workflow.beginnerMode')}
                  </Label>
                  <Switch
                    id="beginner-mode"
                    checked={beginnerMode}
                    onCheckedChange={onBeginnerModeChange}
                    className="scale-90"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs max-w-xs">
                  {t('workflow.beginnerModeTooltip')}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Center: Vehicle Info */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-mono font-bold">{vin}</span>
          <StatusBadge status={status} />
          {stationClaim && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground cursor-default">
                    <UserIcon className="h-3 w-3" />
                    <span>{t('stations.stationLabel', { number: stationClaim.station.station_number })}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-xs">
                    {stationClaim.station.name || t('stations.stationLabel', { number: stationClaim.station.station_number })}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {claimedAt && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{formatRelativeTime(claimedAt)}</span>
            </div>
          )}
        </div>

        {/* Right: Warnings */}
        <div className="flex gap-2 items-center">
          {!hasStation && isCarOwner && (
            <Badge variant="outline" className="text-destructive border-destructive text-xs h-6">
              <AlertTriangle className="mr-1 h-3 w-3" />
              {t('workflow.noStation')}
            </Badge>
          )}
          {!isCarOwner && (
            <Badge variant="outline" className="text-warning border-warning text-xs h-6">
              <AlertTriangle className="mr-1 h-3 w-3" />
              {t('workflow.viewOnly')}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};
